import React, { useContext } from "react";

import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import OfferAPI from "@/src/api/OfferAPI";
import { ComboboxBase } from "@/src/components/form-fields/Combobox";
import { SwitchFieldBase } from "@/src/components/form-fields/SwitchField";
import ItemStatus from "@/src/utils/ItemStatus";

import PlanDrawerContainerContext, {
  PLAN_DRAWER_POPOVER_CLASS,
} from "../plan-drawer-context";

import type { JSX } from "react";

import type { PlanFormValues } from "@/src/components/offers/offerPlanAdapter";
import type { AppState } from "@/src/reducers";

function PlanMonetizationField(): JSX.Element | null {
  const portalContainer = useContext(PlanDrawerContainerContext);
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const allowSponso = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options?.allow_sponso,
  );
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<PlanFormValues>();
  const enabled = watch("monetization_enabled");
  const advertising = watch("advertising");

  const advertisingQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getAdvertisingList,
    params: [{ projectSlug }],
  });
  const { data: advertisings, isLoading } = useQuery({
    ...advertisingQuery,
    enabled: Boolean(allowSponso) && enabled,
  });

  if (!allowSponso) return null;

  const campaignOptionList =
    advertisings?.data?.map((campaign) => ({
      value: campaign.uuid,
      label: campaign.label,
    })) ?? [];

  const handleToggle = (checked: boolean): void => {
    setValue("monetization_enabled", checked, { shouldDirty: true });
    if (!checked) {
      setValue("advertising", null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const handleSelect = (uuid: string): void => {
    const campaign = advertisings?.data?.find((item) => item.uuid === uuid);
    if (campaign) {
      setValue(
        "advertising",
        {
          uuid: campaign.uuid,
          label: campaign.label,
          type: campaign.type,
          status: ItemStatus.getAdvertisingStatus(campaign),
        },
        { shouldDirty: true, shouldValidate: true },
      );
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <SwitchFieldBase
        checked={enabled}
        description={t("editor-offers:monetizationDescription")}
        label={t("editor-offers:monetizationStatus")}
        onCheckedChange={handleToggle}
      />
      {enabled ? (
        <ComboboxBase
          disabled={isLoading}
          emptyLabel={t("editor-offers:comboboxEmpty")}
          error={errors.advertising?.message}
          label={t("editor-offers:selectMonetizationCampaign")}
          onValueChange={handleSelect}
          options={campaignOptionList}
          placeholder={t("editor-offers:selectMonetizationCampaign")}
          popoverClassName={PLAN_DRAWER_POPOVER_CLASS}
          popoverContainer={portalContainer}
          searchPlaceholder={t("editor-offers:comboboxSearch")}
          value={advertising?.uuid}
        />
      ) : null}
    </div>
  );
}

export default PlanMonetizationField;
